import { useContext, useSyncExternalStore, useCallback } from 'react';
import { useModals, PlumaModalManagerContext } from '@customerio/pluma-components/react';
import { useCopilotSessionContext } from '@/features/copilot';
import { CommandDialog } from './command-dialog';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';

// Persists the last typed query across open/close cycles so reopening restores it.
// Scoped to a workspace ID — stale queries from a previous workspace are discarded.
let _lastInputValue = '';
let _lastInputWorkspaceId: number | undefined;
let _openTimeMs: number | null = null;

const persistInputValue = (value: string) => {
  _lastInputValue = value;
};

function getLastInputValue(workspaceId: number): string {
  if (_lastInputWorkspaceId !== workspaceId) {
    _lastInputValue = '';
    _lastInputWorkspaceId = workspaceId;
  }
  return _lastInputValue;
}

function getCloseTrackingData(workspaceId: number) {
  const hadQuery = getLastInputValue(workspaceId).trim().length > 0;
  const timeOpenMs = _openTimeMs == null ? 0 : Date.now() - _openTimeMs;

  return { hadQuery, timeOpenMs };
}

const noopSubscribe = () => () => {};
const emptySnapshot = () => [];

/**
 * CommandDialog is mounted by Pluma’s managed-modal layer (alongside the main app shell, not
 * inside the authenticated layout outlet). Providers that only wrap authenticated content —
 * e.g. CurrentWorkspaceProvider, CopilotSessionProvider — are therefore not ancestors of the
 * modal. Inside CommandDialog, useCurrentWorkspace() is null, useCopilotSessionContext() is not
 * available, and useAnalytics().track silently no-ops (it reads workspace from context).
 *
 * Anything that depends on that layout-scoped context must be captured in this hook (which runs
 * under the top nav / authenticated tree) and passed through CommandDialogData.
 *
 * Hooks that use Nanostores, the root QueryClient, React Router, SafeNavigateProvider, or
 * ActiveAppContextProvider still work inside CommandDialog without being passed in — e.g.
 * useFeatureFlag, useServicesQuery, useSafeNavigate, useMatches, useActiveBundle.
 */
export function useCommandDialog() {
  const modalApi = useContext(PlumaModalManagerContext);
  const modals = useSyncExternalStore(modalApi?.subscribe ?? noopSubscribe, modalApi?.getSnapshot ?? emptySnapshot);
  const isOpen = modals.some((m) => m.modal === CommandDialog && m.state.isOpen === true);

  const modalsApi = useModals();
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();

  const open = useCallback(
    (options?: { initialInputValue?: string }) => {
      const workspaceId = workspace?.id ?? 0;

      const initialInputValue = options?.initialInputValue ?? getLastInputValue(workspaceId);

      _lastInputValue = initialInputValue;
      _lastInputWorkspaceId = workspaceId;
      _openTimeMs = Date.now();

      modalsApi.open(CommandDialog, {
        session,
        workspace,
        initialInputValue,
        persistInputValue,
        track,
      });
    },
    [modalsApi, session, workspace, track],
  );

  const close = useCallback(() => {
    modalsApi.closeAllOfType(CommandDialog);
  }, [modalsApi]);

  const workspaceId = workspace?.id ?? 0;
  const getCloseTrackingSnapshot = useCallback(() => getCloseTrackingData(workspaceId), [workspaceId]);

  return { isOpen, open, close, getCloseTrackingSnapshot };
}
